import React from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { BRAND } from 'theme';
import { SUN } from './illustrations/palette';
import { HomeModuleTarget } from '../types';
import ModuleLink from './ModuleLink';
import NewBadge from './NewBadge';
import { ARROW_NUDGE, SHADOW, actionPill, cardSurface, featuredLayout, nudgeTransition } from './cardStyles';

interface IProps {
  title: string;
  subtitle: string;
  actionLabel: string;
  target: HomeModuleTarget;
  isNew?: boolean;
}

interface Building {
  x: number;
  y: number;
  width: number;
  cols: number;
  rows: number;
}

// Квартал в viewBox 420×300: дома дотянуты ниже края, чтобы скруглённые низы не были видны.
const VIEW = { width: 420, height: 300, overshoot: 12 };
const WINDOW = { width: 12, height: 14, stepX: 24, stepY: 26, top: 18 };
// Свет при наведении включается поэтажно снизу вверх.
const FLOOR_DELAY_MS = 45;

const FRONT: Building[] = [
  { x: 40, y: 160, width: 110, cols: 4, rows: 4 },
  { x: 170, y: 40, width: 100, cols: 3, rows: 9 },
  { x: 300, y: 110, width: 90, cols: 3, rows: 6 },
];
const BACK = [
  { x: 0, y: 120, width: 70 },
  { x: 250, y: 60, width: 60 },
  { x: 380, y: 140, width: 40 },
];
// Окна, которые горят всегда: дом, колонка, этаж сверху.
const LIT = new Set(['0-1-1', '1-1-2', '1-2-5', '2-1-1', '2-0-4']);

interface HouseWindow {
  key: string;
  x: number;
  y: number;
  isLit: boolean;
  delayMs: number;
}

const floorDelay = (y: number) => Math.round((VIEW.height - y) / WINDOW.stepY) * FLOOR_DELAY_MS;

const windowsOf = (building: Building, index: number): HouseWindow[] => {
  const left = building.x + (building.width - (building.cols - 1) * WINDOW.stepX - WINDOW.width) / 2;
  const windows: HouseWindow[] = [];

  for (let row = 0; row < building.rows; row += 1) {
    for (let col = 0; col < building.cols; col += 1) {
      const key = `${index}-${col}-${row}`;
      const y = building.y + WINDOW.top + row * WINDOW.stepY;
      windows.push({ key, x: left + col * WINDOW.stepX, y, isLit: LIT.has(key), delayMs: floorDelay(y) });
    }
  }

  return windows;
};

const WINDOWS = FRONT.flatMap(windowsOf);

const useStyles = makeStyles((theme) => ({
  card: {
    ...cardSurface(theme),
    ...featuredLayout(theme),
    color: theme.palette.common.white,
    background: `linear-gradient(120deg, ${BRAND.ink} 0%, ${BRAND.inkLight} 55%, ${BRAND.accent} 100%)`,
    boxShadow: SHADOW.featured,
    '&:hover, &.Mui-focusVisible': {
      transform: 'translateY(-2px)',
      boxShadow: SHADOW.featuredLift,
    },
    '&:hover $arrow': ARROW_NUDGE,
    '&:hover $window': {
      fill: SUN,
    },
  },
  skyline: {
    position: 'absolute',
    right: theme.spacing(3),
    bottom: 0,
    height: '88%',
    width: '55%',
    pointerEvents: 'none',
  },
  text: {
    position: 'relative',
    maxWidth: '52%',
  },
  title: {
    fontWeight: 700,
  },
  subtitle: {
    marginTop: theme.spacing(1),
    opacity: 0.85,
  },
  action: {
    ...actionPill(theme),
    color: BRAND.ink,
    backgroundColor: theme.palette.common.white,
  },
  arrow: {
    ...nudgeTransition(theme),
  },
  // В углу по внутренним отступам карточки, над кварталом.
  newBadge: {
    position: 'absolute',
    top: theme.spacing(4),
    right: theme.spacing(5),
  },
  window: {
    fill: 'rgba(255,255,255,0.55)',
    transition: theme.transitions.create('fill', { duration: theme.transitions.duration.standard }),
  },
}));

/** Акцентная карточка дома: фирменный градиент и силуэт квартала; при наведении окна загораются поэтажно. */
const FeaturedHouseCard = ({ title, subtitle, actionLabel, target, isNew }: IProps) => {
  const classes = useStyles();

  return (
    <ModuleLink target={target} className={classes.card}>
      <svg
        className={classes.skyline}
        viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
        preserveAspectRatio="xMaxYMax meet"
        aria-hidden="true"
      >
        <g fill="rgba(255,255,255,0.10)">
          {BACK.map((building) => (
            <rect
              key={building.x}
              x={building.x}
              y={building.y}
              width={building.width}
              height={VIEW.height + VIEW.overshoot - building.y}
              rx="6"
            />
          ))}
        </g>
        <g fill="rgba(255,255,255,0.18)">
          {FRONT.map((building) => (
            <rect
              key={building.x}
              x={building.x}
              y={building.y}
              width={building.width}
              height={VIEW.height + VIEW.overshoot - building.y}
              rx="8"
            />
          ))}
        </g>
        <g>
          {WINDOWS.map((spot) => (
            <rect
              key={spot.key}
              className={spot.isLit ? undefined : classes.window}
              fill={spot.isLit ? SUN : undefined}
              style={spot.isLit ? undefined : { transitionDelay: `${spot.delayMs}ms` }}
              x={spot.x}
              y={spot.y}
              width={WINDOW.width}
              height={WINDOW.height}
              rx="2"
            />
          ))}
        </g>
      </svg>

      <span className={classes.text}>
        <Typography component="span" variant="h4" display="block" className={classes.title}>
          {title}
        </Typography>
        <Typography component="span" variant="body1" display="block" className={classes.subtitle}>
          {subtitle}
        </Typography>
      </span>

      {isNew && <NewBadge onDark className={classes.newBadge} />}

      <span className={classes.action}>
        {actionLabel}
        <ArrowForward fontSize="small" className={classes.arrow} />
      </span>
    </ModuleLink>
  );
};

export default FeaturedHouseCard;
